import cn from "app/utils/classnames";
import { ONE_CHANCE_SETTINGS } from "app/data/game-settings";
import MenuButton from "app/ui/MenuButton";
import { useOneChanceState } from "app/one-chance/useOneChance";
import LetterBoxInteractive from "app/one-chance/LetterBoxInteractive";
import LetterBoxLocked from "app/one-chance/LetterBoxLocked";
import useLetterRevealCost from "app/one-chance/getLetterRevealCost";
import {
  parseUserSpelling as parsePlayerSpelling,
  REVEALED_EMPTY_CHAR,
  UNKNOWN_LETTER_CHAR,
} from "app/one-chance/oneChanceSpellingUtils";

export const NUM_LETTERS_PER_ROW = 10;
const PLACEHOLDER_TEXT = "Start typing...";
const LETTER_BOX_HEIGHT_CLASS = "min-h-12";

interface LetterBoxesProps {
  playerSpelling: string;
  correctSpelling: string;
  isInteractive: boolean;
  onLetterRevealed: (letterIndex: number) => void;
  onLengthRevealed: () => void;
  onLetterTapped: (letterIndex: number) => void;
  caretPos: number;
  className?: string;
}

// The cross-word style boxes that the user types into during the OneChance
// round.
export default function LetterBoxes({
  playerSpelling,
  correctSpelling,
  isInteractive,
  onLetterRevealed,
  onLengthRevealed,
  onLetterTapped,
  caretPos,
  className,
}: LetterBoxesProps) {
  const roundState = useOneChanceState();
  const letterRevealCost = useLetterRevealCost();
  const spelledWord = parsePlayerSpelling(playerSpelling);

  return (
    <div className={cn("w-full", "flex flex-col gap-1")}>
      <div
        className={cn(
          "relative",
          "w-full",
          "grid grid-cols-10 gap-px",
          className,
        )}
      >
        <div // TYPING INSTRUCTIONS
          className={cn(
            "col-span-10",
            "p-1",
            "outline",
            "bg-gray-200",
            "flex justify-evenly items-center gap-2",
          )}
        >
          <p className={cn("text-xs lg:text-sm uppercase")}>
            {/* {roundState.spellingSubmitted ? (
              <>Your spelling:</>
            ) : (
              <>Start typing...</>
            )} */}
            Your spelling:
          </p>
        </div>
        {isInteractive
          ? createInteractiveLetterBoxes(
              playerSpelling,
              onLetterRevealed,
              onLetterTapped,
              letterRevealCost !== -1,
              caretPos,
            ).map((lb) => lb)
          : createLockedLetterBoxes(
              playerSpelling,
              correctSpelling,
              ONE_CHANCE_SETTINGS.numLetterBoxes,
            ).map((lb) => lb)}
        <div // INSTRUCTIONS HOLDER
          className={cn(
            "min-h-10 col-span-10",
            "px-1 pt-0",
            "outline",
            "bg-gray-200",
            "flex justify-evenly items-center gap-2",
          )}
        >
          {roundState.spellingSubmitted ? (
            <p // CORRECT/INCORRECT FEEDBACK
              className={cn("uppercase text-sm lg:text-base")}
            >
              {correctSpelling === spelledWord ? (
                <>Perfect!</>
              ) : (
                <>
                  Correct spelling: <b>{correctSpelling}</b>
                </>
              )}
            </p>
          ) : (
            <>
              <p // INSTRUCTIONS
                className={cn(
                  "text-xs uppercase leading-tight",
                  "shrink",
                  letterRevealCost === -1 ? "opacity-0" : "opacity-100",
                )}
              >
                Press and hold a box to
                <br /> reveal a letter:{" "}
                <span className="font-semibold">-{letterRevealCost}x</span>
              </p>
              <MenuButton // REVEAL LENGTH BUTTON
                className={cn(
                  "text-xs",
                  "bg-white",
                  "h-8",
                  "px-1",
                  "flex justify-center gap-2",
                  roundState.lengthRevealed || roundState.spellingSubmitted
                    ? "pointer-events-none opacity-0"
                    : "pointer-events-auto opacity-100",
                )}
                elevation="sm"
                onClick={onLengthRevealed}
              >
                <p className={cn("leading-none")}>
                  Reveal
                  <br /> length
                </p>
                <p>-{ONE_CHANCE_SETTINGS.lengthMultCost}x</p>
              </MenuButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function createInteractiveLetterBoxes(
  playerSpelling: string,
  onRevealed: (letterIndex: number) => void,
  onTapped: (letterIndex: number) => void,
  allowReveal: boolean,
  caretPos: number,
): Array<React.ReactNode> {
  const boxes = [];

  // Show placeholder text if nothing has been typed yet
  const usePlaceholderText =
    playerSpelling.split("").filter((c) => c !== UNKNOWN_LETTER_CHAR).length ===
    0;

  for (let i = 0; i < playerSpelling.length; i++) {
    const char = playerSpelling[i];
    const letter = char === UNKNOWN_LETTER_CHAR ? "" : playerSpelling[i];

    boxes.push(
      <LetterBoxInteractive
        key={i}
        letter={letter}
        allowReveal={allowReveal}
        showCaret={i === caretPos}
        placeholderLetter={usePlaceholderText ? PLACEHOLDER_TEXT[i] : ""}
        onRevealed={() => onRevealed(i)}
        onTapped={() => onTapped(i)}
        heightClass={LETTER_BOX_HEIGHT_CLASS}
      />,
    );
  }

  return boxes;
}

function createLockedLetterBoxes(
  inputString: string,
  correctSpelling: string,
  numBoxes: number,
): Array<React.ReactNode> {
  const boxes = [];
  const playerSpelling = parsePlayerSpelling(inputString);

  for (let i = 0; i < numBoxes; i++) {
    // The letter should reflect the user's spelling
    const letter =
      i < playerSpelling.length ? playerSpelling[i] : REVEALED_EMPTY_CHAR;

    boxes.push(
      <LetterBoxLocked
        key={i}
        letter={letter}
        isCorrect={letter === correctSpelling[i]}
        heightClass={LETTER_BOX_HEIGHT_CLASS}
      />,
    );
  }

  return boxes;
}
